import { compareGlCodes } from '@/utils/gl-code-sort';

/**
 * Sub-GL hierarchy grouping shared by the financial reports (P&L, GL Report,
 * Trial Balance, Balance Sheet). A dotted code like "1030.05" is treated as a
 * child of its pre-dot parent "1030"; a flat code like "7030" is its own group.
 *
 * This is intentionally ONE level deep — the split is on the FIRST dot only, so
 * "7000.02.01" groups under "7000" alongside "7000.02" (matching the proven P&L
 * `groupByParent` behavior). It is RN-free so it can be unit-tested in plain
 * Vitest.
 */

/** A parent code and the items that roll up under it. */
export interface GlParentGroup<T> {
	/** The pre-dot segment, e.g. "1030" for children "1030.05" / "1030.10". */
	parentCode: string;
	/** The item whose code === parentCode, or null when no bare-parent row exists. */
	parent: T | null;
	/** Items whose code is a dotted child of parentCode, sorted by compareGlCodes. */
	children: T[];
}

/**
 * Group a flat list of GL-coded items under their pre-dot parent code. Groups
 * and children are both ordered by `compareGlCodes` (accountant-style numeric
 * ordering). A group whose bare parent code is absent from the list still
 * surfaces (with `parent === null`) so nothing silently disappears.
 */
export function groupByGlParentCode<T>(items: T[], getCode: (t: T) => string): GlParentGroup<T>[] {
	const groups = new Map<string, T[]>();
	for (const item of items) {
		const code = getCode(item) ?? '';
		const dot = code.indexOf('.');
		const parentCode = dot > 0 ? code.slice(0, dot) : code;
		const bucket = groups.get(parentCode);
		if (bucket) bucket.push(item);
		else groups.set(parentCode, [item]);
	}

	const out: GlParentGroup<T>[] = [];
	for (const [parentCode, bucket] of groups) {
		const parent = bucket.find((it) => (getCode(it) ?? '') === parentCode) ?? null;
		const children = bucket
			.filter((it) => (getCode(it) ?? '') !== parentCode)
			.sort((a, b) => compareGlCodes(getCode(a) ?? '', getCode(b) ?? ''));
		out.push({ parentCode, parent, children });
	}
	out.sort((a, b) => compareGlCodes(a.parentCode, b.parentCode));
	return out;
}
